import React from 'react';
import PropTypes from 'prop-types';

import Fare from '../../../../records/Fare';
import Trip from '../../../../records/Trip';
import Service from '../../../../records/Service';
import { translate as t } from '../../../../lib/I18n';

const SEAT_PATH = 'M5.47875 1C7.04675 1.00001 8.42097 2.04966 8.83324 3.5625L10.0422 8H16.0002C16.5523 ' +
    '8.0002 17.0002 8.44784 17.0002 9C17.0002 9.55216 16.5523 9.9998 16.0002 10H10.5871L11.1321 12H18.0032C19.66 ' +
    '12 21.0032 13.3431 21.0032 15C21.0032 16.6569 19.66 18 18.0032 18H13.0002V21H16.0002C16.5523 21.0002 17.0002 ' +
    '21.4478 17.0002 22C17.0002 22.5522 16.5523 22.9998 16.0002 23H8.00023C7.44795 23 7.00023 22.5523 7.00023 ' +
    '22C7.00023 21.4477 7.44795 21 8.00023 21H11.0002V18H7.72582C6.44776 18 5.32748 17.1442 4.99144 ' +
    '15.9111L2.12426 5.39062C1.52166 3.17923 3.18672 1 5.47875 1ZM5.47875 3C4.50528 3 3.79799 3.92602 4.05394 ' +
    '4.86523L6.92113 15.3857C7.02006 15.7484 7.34988 16 7.72582 16H18.0032C18.5554 16 19.0032 15.5523 19.0032 ' +
    '15C19.0032 14.4477 18.5554 14 18.0032 14H9.60472L6.90355 4.08789C6.72834 3.44555 6.14458 3.00001 5.47875 3Z';

const SeatIconAvailable = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d={SEAT_PATH} fill="currentColor" />
    </svg>
);

const SeatIconUnavailable = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d={SEAT_PATH} fill="currentColor" />
        <path
            d="M1.21514 1.21514C1.46574 0.964549 1.85287 0.932644 2.13751 1.12061L2.25209 1.21514L22.7849
            21.7479L22.8794 21.8625C23.0674 22.1471 23.0355 22.5343 22.7849 22.7849C22.5343 23.0355 22.1471
            23.0674 21.8625 22.8794L21.7479 22.7849L1.21514 2.25209L1.12061 2.13751C0.932644 1.85287 0.964549
            1.46574 1.21514 1.21514Z"
            fill="#E50000"
        />
    </svg>
);

const SeatIconPartial = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path fillRule="evenodd" clipRule="evenodd" d={SEAT_PATH} fill="currentColor" />
        <path
            d="M19.9697 7.5332C20.5051 7.53322 20.9392 7.95058 20.9395 8.46582C20.9395 8.98128 20.5053 9.3994
            19.9697 9.39941C19.4342 9.39941 19 8.98129 19 8.46582C19.0003 7.95057 19.4343 7.5332
            19.9697 7.5332ZM19.9697 1C20.5051 1.00002 20.9392 1.41734 20.9395 1.93262V5.66602C20.9395
            6.18147 20.5053 6.59959 19.9697 6.59961C19.4342 6.59961 19 6.18148 19 5.66602V1.93262C19.0002
            1.41733 19.4343 1 19.9697 1Z"
            fill="#E50000"
        />
    </svg>
);

const CONFIG = {
    available: {
        Icon: SeatIconAvailable,
        messageKey: 'booking.basket.seat-availability.available',
    },
    partial: {
        Icon: SeatIconPartial,
        messageKey: 'booking.basket.seat-availability.partial',
    },
    unavailable: {
        Icon: SeatIconUnavailable,
        messageKey: 'booking.basket.seat-availability.unavailable',
    },
};

const combineStatuses = (a, b) => {
    if (!a) { return b; }
    if (!b) { return a; }
    if (a === b) { return a; }
    return 'partial';
};

const getJourneyLegs = (trip) => {
    const outwardLegs = trip && trip.outwardJourney
        ? trip.outwardJourney.getIn(['journey', 'legs'])
        : null;
    const inwardLegs = trip && trip.returnJourney
        ? trip.returnJourney.getIn(['journey', 'legs'])
        : null;

    if (outwardLegs && inwardLegs) { return outwardLegs.concat(inwardLegs); }
    return outwardLegs || inwardLegs || null;
};

export const computeSeatStatus = ({ fare, trip, service, inwardFare }) => {
    const legs = service
        ? service.getIn(['journey', 'legs'])
        : getJourneyLegs(trip);
    const outwardStatus = fare ? fare.getAssignedSeatStatus(legs) : null;
    const inwardStatus = inwardFare ? inwardFare.getAssignedSeatStatus(legs) : null;
    return combineStatuses(outwardStatus, inwardStatus);
};

const SeatAvailabilityStatus = ({ fare, trip, service, inwardFare, status: propStatus, hideIcon, compact }) => {
    const status = propStatus || computeSeatStatus({ fare, trip, service, inwardFare });
    if (!status) { return null; }
    const { Icon, messageKey } = CONFIG[status];

    const classNames = [
        'seat-availability-status',
        `seat-availability-status--${status}`,
        compact ? 'seat-availability-status--compact' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={classNames}>
            {!hideIcon && (
                <span className="seat-availability-status__icon">
                    <Icon />
                </span>
            )}
            <span className="seat-availability-status__text">{t(messageKey)}</span>
        </div>
    );
};

SeatAvailabilityStatus.propTypes = {
    fare: PropTypes.instanceOf(Fare),
    trip: PropTypes.instanceOf(Trip),
    service: PropTypes.instanceOf(Service),
    inwardFare: PropTypes.instanceOf(Fare),
    status: PropTypes.oneOf(['available', 'partial', 'unavailable']),
    hideIcon: PropTypes.bool,
    compact: PropTypes.bool,
};

SeatAvailabilityStatus.defaultProps = {
    fare: null,
    trip: null,
    service: null,
    inwardFare: null,
    status: null,
    hideIcon: false,
    compact: false,
};

export { SeatIconAvailable, SeatIconPartial, SeatIconUnavailable };
export default SeatAvailabilityStatus;
